import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Filler,
    Tooltip,
    Legend
} from 'chart.js';
import { useNavigate } from "react-router-dom";
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PageLayout from '../../components/layout/PageLayout';
import ChartCard from '../../components/ui/ChartCard';
import StatTile from '../../components/ui/StatTile';
import StatusChip from '../../components/ui/StatusChip';
import BreakdownList from '../../components/ui/BreakdownList';
import { baseChartOptions, barDataset, lineDataset } from '../../components/ui/chartConfig';
import { statusImportacao } from '../../components/importacoes/ImportacoesListPage';
import { statusAssinatura } from '../../utils/StatusAssinatura';
import { chartColors } from '../../theme';
import { calcularMetricas } from './metricas';

ChartJS.register(BarElement, CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip, Legend);

const formatBRL = (v, casas = 2) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: casas, maximumFractionDigits: casas });
const formatPct = (v) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const formatNum = (v) => Number(v || 0).toLocaleString('pt-BR');
const formatDataCurta = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const formatDataHora = (v) => {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Fontes de dados (todas já existentes no back). `chave` = campo da resposta com a lista.
const FONTES = [
    { id: 'assinaturas', url: '/listarAssinaturas', chave: 'assinaturas', auth: true, nome: 'assinaturas' },
    { id: 'planos', url: '/listarPlanos', chave: 'planos', auth: false, nome: 'planos' },
    { id: 'cancelamentos', url: '/listarCancelamentos', chave: 'cancelamentos', auth: true, nome: 'cancelamentos' },
    { id: 'clientes', url: '/listarUsuariosEmitirNota', chave: 'usuarios', auth: true, nome: 'clientes' },
    { id: 'leadsLanding', url: '/api/listarLeads', chave: 'leads', auth: true, nome: 'leads da landing page' },
    { id: 'leadsPerdidos', url: '/leads/recuperaveis', chave: null, auth: false, nome: 'leads perdidos' },
    { id: 'importacoesGuias', url: '/listar/importacoes', chave: 'importacoes', auth: true, nome: 'importações de guias' },
    { id: 'importacoesDemonstracoes', url: '/listar/importacoes/demonstracoes', chave: 'importacoes', auth: true, nome: 'importações de demonstrações' },
];

const buscarFonte = async (fonte, token) => {
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}${fonte.url}`, {
        headers: fonte.auth ? { Authorization: token } : undefined,
    });
    if (fonte.chave === null) return Array.isArray(data) ? data : [];
    // Algumas rotas respondem status 400 quando a lista está vazia.
    if (data?.status === 403) throw new Error('Acesso negado');
    return Array.isArray(data?.[fonte.chave]) ? data[fonte.chave] : [];
};

function ResumoImportacao({ titulo, resumo, onVer, loading }) {
    const ultima = resumo?.ultima;
    const status = statusImportacao(ultima?.status);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">{titulo}</Typography>
                <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />} onClick={onVer} sx={{ color: 'text.secondary' }}>
                    Ver
                </Button>
            </Box>
            {loading ? (
                <Skeleton variant="rounded" height={64} />
            ) : !ultima ? (
                <Typography variant="body2" color="text.secondary">Nenhuma importação realizada.</Typography>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 1, columnGap: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Última importação</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDataHora(ultima.criadoEm)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Status</Typography>
                        <StatusChip label={status.label} tone={status.tone} />
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Arquivos (30 dias)</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatNum(resumo.arquivos30)} em {formatNum(resumo.importacoes30)} importação(ões)</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Erros (30 dias)</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: resumo.erros30 > 0 ? 'error.main' : 'text.primary' }}>
                            {formatNum(resumo.erros30)} ({formatPct(resumo.taxaErro30)})
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

const Dashboard = () => {
    const navigate = useNavigate();
    const [dados, setDados] = useState({});
    const [falhas, setFalhas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [atualizadoEm, setAtualizadoEm] = useState(null);

    const carregar = useCallback(async () => {
        const token = localStorage.getItem('token');
        setLoading(true);
        const resultados = await Promise.allSettled(FONTES.map((f) => buscarFonte(f, token)));
        const novosDados = {};
        const novasFalhas = [];
        resultados.forEach((r, i) => {
            if (r.status === 'fulfilled') {
                novosDados[FONTES[i].id] = r.value;
            } else {
                novosDados[FONTES[i].id] = [];
                novasFalhas.push(FONTES[i].nome);
                console.error(`Erro ao buscar ${FONTES[i].nome}:`, r.reason);
            }
        });
        setDados(novosDados);
        setFalhas(novasFalhas);
        setAtualizadoEm(new Date());
        setLoading(false);
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const m = useMemo(() => calcularMetricas(dados), [dados]);

    const alturaBarras = (n) => Math.max(200, Math.min(n * 34 + 40, 360));

    const evolucaoData = {
        labels: m.meses,
        datasets: [
            barDataset('Novas assinaturas', m.assinaturas.novasPorMes, chartColors.primary),
            barDataset('Cancelamentos', m.cancelamentos.porMes, chartColors.secondary),
        ],
    };

    return (
        <PageLayout
            title="Dashboard"
            subtitle="Visão geral de clientes, assinaturas, receita e operação contábil."
            actions={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {atualizadoEm && (
                        <Typography variant="body2" color="text.secondary">
                            Atualizado às {atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                    )}
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={carregar} loading={loading} loadingPosition="start">
                        Atualizar
                    </Button>
                </Box>
            }
        >
            {falhas.length > 0 && !loading && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 2, bgcolor: 'background.paper' }}>
                    Não foi possível carregar: {falhas.join(', ')}. Os indicadores relacionados podem estar incompletos.
                </Alert>
            )}

            {/* KPIs */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                <StatTile
                    label="Assinaturas ativas"
                    icon={CardMembershipOutlinedIcon}
                    loading={loading}
                    value={formatNum(m.assinaturas.ativas)}
                    helper={`${formatNum(m.assinaturas.novas30)} nova(s) em 30 dias · ${formatNum(m.assinaturas.pendentes)} pendente(s)`}
                    onClick={() => navigate('/Assinaturas')}
                />
                <StatTile
                    label="Cancelamentos (30 dias)"
                    icon={TrendingDownOutlinedIcon}
                    loading={loading}
                    value={formatNum(m.cancelamentos.ultimos30)}
                    helper={`Taxa de cancelamento ${formatPct(m.cancelamentos.taxa30)} · ${formatNum(m.cancelamentos.total)} no total`}
                />
                <StatTile
                    label="Clientes cadastrados"
                    icon={PeopleAltOutlinedIcon}
                    loading={loading}
                    value={formatNum(m.clientes.total)}
                    helper={`${formatNum(m.clientes.comEmpresa)} com empresa · ${formatNum(m.clientes.semAssinaturaAtiva)} sem assinatura ativa`}
                    onClick={() => navigate('/Usuarios')}
                />
            </Box>

            {/* Evolução + próximas cobranças */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2, mb: 2 }}>
                <ChartCard
                    title="Novas assinaturas e cancelamentos"
                    description="Quantidade por mês nos últimos 12 meses."
                    loading={loading}
                    empty={m.assinaturas.total === 0 && m.cancelamentos.total === 0}
                    height={280}
                >
                    <Bar data={evolucaoData} options={baseChartOptions({ legend: true })} />
                </ChartCard>

                <Card sx={{ p: 2.5, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Box>
                            <Typography variant="h5" component="h2">Próximas cobranças</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>Assinaturas ativas com cobrança nos próximos 7 dias.</Typography>
                        </Box>
                        <EventOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </Box>
                    {loading ? (
                        <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: 3, mt: 2, mb: 1.5 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Cobranças</Typography>
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>{formatNum(m.assinaturas.proximasCobrancas.length)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Valor previsto</Typography>
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>{formatBRL(m.assinaturas.valorProximasCobrancas)}</Typography>
                                </Box>
                            </Box>
                            <Divider />
                            <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 220 }}>
                                {m.assinaturas.proximasCobrancas.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                        Nenhuma cobrança prevista para os próximos 7 dias.
                                    </Typography>
                                ) : (
                                    m.assinaturas.proximasCobrancas.map((c) => (
                                        <Box key={c.idAssinatura} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                            <Box sx={{ width: 44, flexShrink: 0, textAlign: 'center', py: 0.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>{formatDataCurta(c.data)}</Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }} noWrap>{c.cliente}</Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap component="div">{c.plano}</Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatBRL(c.valor)}</Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </>
                    )}
                </Card>
            </Box>

            {/* Planos e receita */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
                <ChartCard
                    title="Assinaturas ativas por plano"
                    loading={loading}
                    empty={m.assinaturas.ativasPorPlano.length === 0}
                    emptyMessage="Nenhuma assinatura ativa."
                    height={alturaBarras(m.assinaturas.ativasPorPlano.length)}
                >
                    <Bar
                        data={{ labels: m.assinaturas.ativasPorPlano.map((i) => i.label), datasets: [barDataset('Assinaturas ativas', m.assinaturas.ativasPorPlano.map((i) => i.value))] }}
                        options={baseChartOptions({ horizontal: true })}
                    />
                </ChartCard>

                <ChartCard
                    title="Receita mensal por plano"
                    description="Estimada pelo preço atual de cada plano."
                    loading={loading}
                    empty={m.assinaturas.receitaPorPlano.length === 0}
                    emptyMessage="Nenhuma assinatura ativa."
                    height={alturaBarras(m.assinaturas.receitaPorPlano.length)}
                >
                    <Bar
                        data={{ labels: m.assinaturas.receitaPorPlano.map((i) => i.label), datasets: [barDataset('Receita mensal', m.assinaturas.receitaPorPlano.map((i) => Number(i.value.toFixed(2))))] }}
                        options={baseChartOptions({ horizontal: true, currency: true })}
                    />
                </ChartCard>

                <Card sx={{ p: 2.5, gridColumn: { md: '1 / -1', xl: 'auto' } }}>
                    <Typography variant="h5" component="h2">Perfil das assinaturas</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>Distribuição por status, forma de pagamento e periodicidade.</Typography>
                    {loading ? (
                        <Skeleton variant="rounded" height={240} />
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)', xl: '1fr' }, gap: 2.5 }}>
                            <BreakdownList
                                title="Status (todas)"
                                items={m.assinaturas.status.map((s) => {
                                    const st = statusAssinatura(s.label);
                                    return { ...s, adornment: <StatusChip label={st.label} tone={st.tone} /> };
                                })}
                            />
                            <BreakdownList title="Forma de pagamento (ativas)" items={m.assinaturas.metodosPagamento} />
                            <BreakdownList title="Periodicidade (ativas)" items={m.assinaturas.periodicidades} />
                        </Box>
                    )}
                </Card>
            </Box>

            {/* Conversão e retenção */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <ChartCard
                    title="Leads perdidos por etapa"
                    description={`${formatNum(m.leads.perdidos)} lead(s) iniciaram o cadastro e não concluíram.`}
                    loading={loading}
                    empty={m.leads.porEtapa.length === 0}
                    action={
                        <Button size="small" variant="outlined" endIcon={<ArrowForwardIcon fontSize="small" />} onClick={() => navigate("../Dashboard/Leads")}>
                            Ver leads
                        </Button>
                    }
                >
                    <Bar
                        data={{ labels: m.leads.porEtapa.map((i) => i.label), datasets: [barDataset('Leads que pararam nesta etapa', m.leads.porEtapa.map((i) => i.value))] }}
                        options={baseChartOptions({ onClick: () => navigate("../Dashboard/Leads") })}
                    />
                </ChartCard>

                <ChartCard
                    title="Motivos de cancelamento"
                    description="Quantidade de clientes por motivo informado (um cliente pode marcar mais de um)."
                    loading={loading}
                    empty={m.cancelamentos.motivos.length === 0}
                    height={alturaBarras(m.cancelamentos.motivos.length)}
                >
                    <Bar
                        data={{ labels: m.cancelamentos.motivos.map((i) => i.label), datasets: [barDataset('Clientes', m.cancelamentos.motivos.map((i) => i.value))] }}
                        options={baseChartOptions({ horizontal: true })}
                    />
                </ChartCard>
            </Box>

            {/* Aquisição e operação */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2 }}>
                <ChartCard
                    title="Leads da landing page"
                    description={`${formatNum(m.leads.landing30)} formulário(s) nos últimos 30 dias · ${formatNum(m.leads.landingTotal)} no total.`}
                    loading={loading}
                    empty={m.leads.landingTotal === 0}
                    height={260}
                    action={
                        <Button size="small" variant="outlined" endIcon={<ArrowForwardIcon fontSize="small" />} onClick={() => navigate("/LeadsLandingPage")}>
                            Detalhes
                        </Button>
                    }
                >
                    <Line
                        data={{ labels: m.meses, datasets: [lineDataset('Formulários enviados', m.leads.landingPorMes)] }}
                        options={baseChartOptions()}
                    />
                </ChartCard>

                <Card sx={{ p: 2.5 }}>
                    <Typography variant="h5" component="h2">Operação contábil</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>Situação das últimas importações.</Typography>
                    <ResumoImportacao
                        titulo="Guias (obrigações)"
                        resumo={m.importacoes.guias}
                        loading={loading}
                        onVer={() => navigate('/obrigacoes')}
                    />
                    <Divider sx={{ my: 2 }} />
                    <ResumoImportacao
                        titulo="Demonstrações contábeis"
                        resumo={m.importacoes.demonstracoes}
                        loading={loading}
                        onVer={() => navigate('/demonstracoes-contabeis')}
                    />
                </Card>
            </Box>
        </PageLayout>
    );
};

export default Dashboard;
